import { Button, Field, Input, Spinner, Text } from '@fluentui/react-components';
import { type SignInState } from '@gandhi/mail-protocol';
import { BrandWordmark } from '@gandhi-group/ui';
import { useState, type FormEvent, type ReactElement } from 'react';
import { ApiError, type MailApiClient } from '../api/client.js';

export interface SignInProps {
  readonly api: MailApiClient;
  readonly state: SignInState;
  readonly onSignedIn: (state: SignInState) => void;
}

/**
 * The only sign-in there is. The mail server checks the address and the password,
 * and until it accepts them the app holds nothing and shows nothing.
 */
export function SignIn({ api, state, onSignedIn }: SignInProps): ReactElement {
  const [address, setAddress] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);

  const locked = state.mailboxes.filter((m) => !m.unlocked).map((m) => m.address);
  const known = locked[0];
  const ready = password !== '' && (known !== undefined || address !== '');

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    setProblem(undefined);
    try {
      onSignedIn(await api.signIn(password, known === undefined ? address : undefined));
    } catch (error) {
      setProblem(describe(error));
      setPassword('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={(e) => void submit(e)}
      style={{
        display: 'grid',
        placeContent: 'center',
        justifyItems: 'start',
        gap: 16,
        height: '100vh',
        padding: 24,
        maxWidth: 420,
        margin: '0 auto',
      }}
    >
      <BrandWordmark />
      <Text as="h1" size={500} weight="semibold">
        Open your mail
      </Text>
      <Text size={200}>
        {known === undefined
          ? 'Your email address and the password you use to sign in.'
          : `Enter the password for ${known}.`}
      </Text>

      {known === undefined && (
        <Field label="Email address" style={{ width: '100%' }}>
          <Input
            type="email"
            value={address}
            autoFocus
            autoComplete="username"
            disabled={busy}
            onChange={(_e, data) => setAddress(data.value)}
          />
        </Field>
      )}

      <Field
        label="Password"
        style={{ width: '100%' }}
        {...(problem === undefined
          ? {}
          : { validationState: 'error' as const, validationMessage: problem })}
      >
        <Input
          type="password"
          value={password}
          autoFocus={known !== undefined}
          autoComplete="current-password"
          disabled={busy}
          onChange={(_e, data) => setPassword(data.value)}
        />
      </Field>

      <Button type="submit" appearance="primary" disabled={busy || !ready}>
        {busy ? <Spinner size="tiny" label="Opening…" /> : 'Open mail'}
      </Button>
    </form>
  );
}

/**
 * A refusal and an outage need different words. Telling someone their password is
 * wrong when the server is unreachable sends them to change a password that was
 * never the problem.
 */
export function describe(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === 'UNAUTHENTICATED') {
      return 'That address and password were not accepted.';
    }
    if (error.code === 'BACKEND_UNAVAILABLE') {
      return 'The mail server did not answer, so your password was never checked. Try again shortly.';
    }
    return error.message;
  }
  return 'Something went wrong. Try again shortly.';
}
